/**
 * Smoothness for freehand LEATHER geometry (#1016, epic #781) — part outlines,
 * cutouts and seams.
 *
 * Freehand construction lines have had this since #171. Leather had nothing:
 * `pathFromBuffer` joined every pointer sample with `L` commands, so a traced
 * outline was a raw polyline of pointer positions with no way to clean up a
 * shaky hand afterwards.
 *
 * The contract is construction's, deliberately, rather than a second design:
 * the trace is kept in `rawPoints`, the visible geometry is a DERIVED view
 * re-computed on every commit, and `smoothness` is the simplify tolerance
 * (0 = the raw polyline). Deriving rather than baking is the whole point — it
 * is what keeps the slider adjustable after the fact, so a trace can be pulled
 * smooth and back again. The first destructive edit drops both fields and the
 * object becomes ordinary geometry.
 *
 * SEAMS smooth into CURVES. Since #982 a seam carries bezier handles, so
 * `simplify` gives it few anchors plus handles rather than a re-sampled
 * polyline — which is both what the user drew and far easier to edit
 * afterwards. Hole placement already walks curves, so nothing downstream
 * changes.
 */
import type paper from "paper";
import type { Cutout, Piece, Project, StitchLine, Vec2 } from "./Project";
import { pieceStitchLines } from "./Project";
import {
  DEFAULT_FREEHAND_SMOOTHNESS,
  MAX_FREEHAND_SMOOTHNESS,
  pointsToPolyline,
  smoothPathFromRawPoints,
} from "./freehandSmoothing";
import { seamFromPathData } from "./seamGeometry";

/** Leather uses the SAME scale as construction (#1016 follow-up) — one meaning
 *  for one gesture. Re-exported under the leather names the panels already use;
 *  the numbers and the reasoning live in `freehandSmoothing.ts`. */
export const DEFAULT_LEATHER_SMOOTHNESS = DEFAULT_FREEHAND_SMOOTHNESS;
export const MAX_LEATHER_SMOOTHNESS = MAX_FREEHAND_SMOOTHNESS;

/** True when this object still carries a re-derivable freehand trace. */
export function hasFreehandSource(obj: {
  rawPoints?: Vec2[];
  smoothness?: number;
}): boolean {
  return !!obj.rawPoints && obj.rawPoints.length >= 2;
}

/** True when this object's visible geometry is still a DERIVED view of its
 *  trace — the question the derive pass asks, as opposed to
 *  {@link hasFreehandSource}, which is the question the panel asks.
 *
 *  They used to be the same question. A hand edit dropped the trace, so
 *  "has a trace" and "is derived from it" could not disagree, and the
 *  Smoothness control disappeared for good the first time you moved a point
 *  (#248). The trace is kept now, so the two come apart: the control stays,
 *  and the derive stands down until the control is used again. */
export function derivesFromFreehandSource(obj: {
  rawPoints?: Vec2[];
  smoothness?: number;
  freehandEdited?: boolean;
}): boolean {
  return hasFreehandSource(obj) && !obj.freehandEdited;
}

/** Note that the geometry has been hand-edited away from the trace (#248).
 *
 *  KEEPS the trace: re-applying Smoothness afterwards is the whole point of
 *  the ticket. What it stops is the derive pass running on its own, which
 *  would otherwise rebuild the geometry from the trace on the very next commit
 *  and undo the edit that just happened. Re-applying Smoothness clears the
 *  flag and discards the hand edits — that trade is the user's to make, and it
 *  is why the panel says so next to the control. */
export function markFreehandEdited<
  T extends { rawPoints?: Vec2[]; smoothness?: number; freehandEdited?: boolean },
>(obj: T): T {
  if (!hasFreehandSource(obj) || obj.freehandEdited) return obj;
  return { ...obj, freehandEdited: true };
}

/** Drop the freehand source entirely, making the current geometry the truth.
 *  Kept for the edits that genuinely END the relationship — a slice hands each
 *  half geometry the original trace never described, so there is nothing left
 *  to re-smooth from. */
export function clearFreehandSource<
  T extends { rawPoints?: Vec2[]; smoothness?: number; freehandEdited?: boolean },
>(obj: T): T {
  if (!obj.rawPoints && obj.smoothness === undefined) return obj;
  const next = { ...obj };
  delete next.rawPoints;
  delete next.smoothness;
  delete next.freehandEdited;
  return next;
}

/** The seam geometry (anchors + handles) a trace smooths into. */
export function smoothSeamFromRawPoints(
  rawPoints: Vec2[],
  smoothness: number,
  closed: boolean,
  scope: paper.PaperScope,
): { points: Vec2[]; handles?: StitchLine["handles"] } | null {
  if (rawPoints.length < 2) return null;
  if (smoothness <= 0) {
    return { points: rawPoints.map((p) => ({ x: p.x, y: p.y })) };
  }
  const d = smoothPathFromRawPoints(rawPoints, smoothness, closed, scope);
  const seam = seamFromPathData(d, scope);
  // A tolerance high enough to collapse the trace below two anchors would leave
  // no seam at all; fall back to the raw trace rather than losing it.
  if (!seam || seam.points.length < 2) {
    return { points: rawPoints.map((p) => ({ x: p.x, y: p.y })) };
  }
  return { points: seam.points, handles: seam.handles };
}

/**
 * Re-derive every freehand-sourced leather object's geometry from its trace.
 *
 * Returns the input project unchanged when nothing needs updating, so the
 * overwhelmingly common no-freehand commit costs a walk and nothing else —
 * the same memoization contract the sibling derive passes use.
 */
export function deriveLeatherSmoothing(
  project: Project,
  scope: paper.PaperScope,
): Project {
  if (project.pieces.length === 0) return project;
  let changed = false;
  const pieces: Piece[] = [];

  for (const piece of project.pieces) {
    let nextPiece = piece;

    // A part outline is always a closed region.
    if (derivesFromFreehandSource(piece) && !piece.constructionOutline) {
      const d = smoothPathFromRawPoints(
        piece.rawPoints!,
        piece.smoothness ?? 0,
        true,
        scope,
      );
      if (d && d !== piece.shapePathData) {
        nextPiece = { ...nextPiece, shapePathData: d };
        changed = true;
      }
    }

    const cutouts: Cutout[] = [];
    let cutoutsChanged = false;
    for (const cut of piece.cutouts ?? []) {
      if (!derivesFromFreehandSource(cut) || cut.constructionOutline) {
        cutouts.push(cut);
        continue;
      }
      const d = smoothPathFromRawPoints(
        cut.rawPoints!,
        cut.smoothness ?? 0,
        true,
        scope,
      );
      if (d && d !== cut.pathData) {
        cutouts.push({ ...cut, pathData: d });
        cutoutsChanged = true;
      } else {
        cutouts.push(cut);
      }
    }
    if (cutoutsChanged) {
      nextPiece = { ...nextPiece, cutouts };
      changed = true;
    }

    const seams: StitchLine[] = [];
    let seamsChanged = false;
    for (const seam of pieceStitchLines(piece)) {
      if (!derivesFromFreehandSource(seam) || seam.constructionOutline) {
        seams.push(seam);
        continue;
      }
      const derived = smoothSeamFromRawPoints(
        seam.rawPoints!,
        seam.smoothness ?? 0,
        seam.closed === true,
        scope,
      );
      if (!derived) {
        seams.push(seam);
        continue;
      }
      // Compare on the serialised form so an unchanged re-derive doesn't
      // re-snapshot the project on every commit.
      const same =
        pointsToPolyline(derived.points, false) ===
          pointsToPolyline(seam.points, false) &&
        JSON.stringify(derived.handles ?? null) ===
          JSON.stringify(seam.handles ?? null);
      if (same) {
        seams.push(seam);
        continue;
      }
      seams.push({ ...seam, points: derived.points, handles: derived.handles });
      seamsChanged = true;
    }
    if (seamsChanged) {
      nextPiece = { ...nextPiece, stitchLines: seams };
      changed = true;
    }

    pieces.push(nextPiece);
  }

  return changed ? { ...project, pieces } : project;
}
